import {
  DEFAULT_SIZE_TARGETS,
  type BrandDiscoveryPreferences,
  type Niche,
  type RecommendationDto,
  type SizeTarget,
} from '@fluenceur/types';
import {
  EXAMPLE_PERSONAS,
  FOLLOWERS_BY_SIZE,
  NICHE_TO_PERSONA,
  type PersonaKey,
} from './example-personas';

// ---------------------------------------------------------------------------
// Geo → city mapping
// ---------------------------------------------------------------------------

const GEO_TO_CITY: Record<string, string> = {
  FR: 'Paris',
  BE: 'Bruxelles',
  CH: 'Genève',
  US: 'New York',
  GB: 'Londres',
  DE: 'Berlin',
  IT: 'Milan',
  ES: 'Madrid',
  CA: 'Montréal',
};

function resolveCityFromGeo(geo: string[]): string {
  return GEO_TO_CITY[geo[0] ?? ''] ?? 'Paris';
}

// ---------------------------------------------------------------------------
// Tier selection from persona key order
// ---------------------------------------------------------------------------

const CARD_TIERS: Array<'excellent' | 'good'> = ['excellent', 'excellent', 'good'];

// ---------------------------------------------------------------------------
// Follower count: cycle sizeTargets across card index
// ---------------------------------------------------------------------------

function followersForCard(sizeTargets: SizeTarget[], cardIndex: number): number {
  const sizes = sizeTargets.length > 0 ? sizeTargets : (['micro'] as SizeTarget[]);
  const size = sizes[cardIndex % sizes.length];
  return FOLLOWERS_BY_SIZE[size][cardIndex % 3];
}

// ---------------------------------------------------------------------------
// Base shape shared across all preview recommendations
// ---------------------------------------------------------------------------

const BASE_PREVIEW: Omit<
  RecommendationDto,
  | 'id'
  | 'socialProfileId'
  | 'rank'
  | 'finalScore'
  | 'rawScore'
  | 'matchTier'
  | 'brief'
  | 'creatorProfile'
> = {
  brandId: 'mock-brand-001',
  batchId: 'mock-batch-preview',
  briefSignals: ['niche_match', 'tone_match', 'geo_match'],
  briefSource: 'prefs-extractor',
  fallback: false,
  modelVersion: 'gemini-2.5-flash-lite',
  brandDnaHash: 'mockhash0000000000000000000000000000000000000000000000000000000000000000',
  deliveredAt: '2026-06-11T08:00:00.000Z',
  dismissedAt: null,
  qualificationStatus: 'pending',
};

/** The default trio: diverse, one man among them. */
const DEFAULT_FALLBACK_PERSONAS: readonly PersonaKey[] = ['maya', 'noah', 'lena'];

/** Three women, for copy that speaks of « créatrices » (Discovery's UGC mode). */
export const FEMALE_EXAMPLE_PERSONAS: readonly PersonaKey[] = ['maya', 'lena', 'ines'];

/** Preferences of a brand that stated none: the examples fall back to their default trio. */
export const NO_EXAMPLE_PREFERENCES: BrandDiscoveryPreferences = {
  sizeTargets: [...DEFAULT_SIZE_TARGETS],
  nicheTags: [],
  tone: [],
  antiNiche: [],
  geo: [],
};

/**
 * Build exactly 3 example RecommendationDto objects from brand preferences.
 *
 * The builder stays pure: instead of calling `t()` it takes a `translate`
 * function and resolves each persona brief through it, so the caller
 * (PromiseBlock) owns all i18n. Pass `(key, vars) => t(key, vars)`.
 */
export function buildExampleRecommendations(
  prefs: BrandDiscoveryPreferences,
  translate: (key: string, vars: Record<string, string>) => string,
  /** Personas completing the trio when the niches name fewer than three. */
  fallbacks: readonly PersonaKey[] = DEFAULT_FALLBACK_PERSONAS
): RecommendationDto[] {
  const city = resolveCityFromGeo(prefs.geo);

  // 1. Collect persona keys in niche order, deduplicated
  const candidateKeys: PersonaKey[] = [];
  for (const niche of prefs.nicheTags) {
    const key = NICHE_TO_PERSONA[niche as Niche];
    if (key && !candidateKeys.includes(key)) {
      candidateKeys.push(key);
    }
  }

  // 2. Fill to 3 with the fallbacks (no duplicates)
  for (const fb of fallbacks) {
    if (candidateKeys.length >= 3) break;
    if (!candidateKeys.includes(fb)) candidateKeys.push(fb);
  }

  const selected = candidateKeys.slice(0, 3) as [PersonaKey, PersonaKey, PersonaKey];

  return selected.map((personaKey, i) => {
    const persona = EXAMPLE_PERSONAS[personaKey];
    const briefI18nKey = `companion.wizard.ready.examples.${persona.briefKey}`;
    const brief = translate(briefI18nKey, { city });
    const followersCount = followersForCard(prefs.sizeTargets as SizeTarget[], i);

    return {
      ...BASE_PREVIEW,
      id: `mock-rec-preview-${String(i + 1).padStart(3, '0')}`,
      socialProfileId: `mock-creator-${personaKey}`,
      rank: i + 1,
      finalScore: [92, 88, 81][i],
      rawScore: [0.87, 0.82, 0.74][i],
      matchTier: CARD_TIERS[i],
      brief,
      creatorProfile: {
        platformUserId: null,
        username: persona.username,
        displayName: persona.displayName,
        profilePictureUrl: persona.profilePictureUrl,
        followersCount,
        engagementRate: [0.047, 0.061, 0.038][i],
        country: prefs.geo[0] ?? 'FR',
        isVerified: false,
        accountType: 'CREATOR',
      },
    };
  });
}
